import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { commentHasCodeSuggestions } from 'src/components/code-reviewer-feedback/src/util';
import { ConversationProps } from 'src/components/conversation';
import {
  commentsInThread,
  CommentThread,
} from 'src/components/conversation/src/components/comment-tree';
import {
  DEFAULT_CODE_REVIEWER_COMMENT_UUIDS,
  identifyCodeReviewerComment,
} from 'src/components/conversation/src/utils/code-reviewer-comment-identifiers';
import {
  ApiComment,
  FabricComment,
  isApiComment,
} from 'src/components/conversation-provider/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import { useIntl } from 'src/hooks/intl';
import { TOGGLE_CREATE_COMMENT_TASK_INPUT } from 'src/redux/pull-request/actions';
import {
  getCanLikeComments,
  getCommentLikes,
  getCurrentPullRequest,
  getIsCurrentPullRequestOpen,
} from 'src/redux/pull-request/selectors';
import { useCreateJiraIssueAction } from 'src/sections/repository/sections/jira/hooks/use-create-jira-issue-action';
import { getSelectedCommentText } from 'src/sections/repository/utils/selected-comment-text';
import { getCanCreateTask } from 'src/selectors/task-selectors';
import { getCurrentUserKey } from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';
import { publishUiEvent } from 'src/utils/analytics/publish';

import {
  PullRequestCommentLikeButton,
  PullRequestCommentLikers,
} from '../../components/comment-likes/comment-likes';
import {
  getCommentLikers,
  isCommentLikedByUser,
  toggleCommentLikeAction,
} from '../../components/comment-likes/utils';
import messages from '../../components/pull-request-conversation.i18n';

type RenderAdditionalCommentActions =
  ConversationProps['renderAdditionalCommentActions'];

const useCommentLikeActions = (threads: CommentThread[]) => {
  const comments = threads.flatMap(thread =>
    commentsInThread(thread).filter<ApiComment>(isApiComment)
  );
  const pullrequest = useSelector(getCurrentPullRequest);
  const dispatch = useDispatch();
  const currentUserUuid = useSelector(getCurrentUserKey);
  const canLikeComments = useSelector(getCanLikeComments);
  // TODO look into BasePullRequestCoversation being re-rendered because this useSelector
  // hook may not be memoized correctly. Debug this by visiting a PR with the query param
  // ?wdyr=BasePullRequestCoversation while running Frontbucket in dev mode.
  const commentLikes = useSelector((state: BucketState) =>
    getCommentLikes(state, comments)
  );

  const isArFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );

  const renderActions: RenderAdditionalCommentActions = useCallback(
    (CommentAction: React.ComponentType<any>, comment: FabricComment) => {
      // We just want the PR data here, and we are assuming that
      // the PR data across all comments in this thread is the same.
      // Would love to know if there was a better way of doing this.
      const commentActions: JSX.Element[] = [];
      const { commentId } = comment;

      const handleOnLikeCommentActionClick = () => {
        const isLiked = isCommentLikedByUser(
          commentId,
          commentLikes,
          currentUserUuid
        );

        const codeReviewerFeedbackAttributes =
          identifyCodeReviewerComment(comment.createdBy, commentUUIDs) &&
          isArFeedbackEnabled &&
          pullrequest
            ? {
                isCodeReviewer: true,
                codeReviewerFeedbackSentiment: isLiked
                  ? 'negative'
                  : 'positive',
                prId: pullrequest.id,
                prURL: pullrequest.links.html.href,
                prAuthorId: pullrequest.author?.account_id,
                hasCodeSuggestions: commentHasCodeSuggestions(comment),
              }
            : null;

        dispatch(
          toggleCommentLikeAction(
            commentId,
            commentLikes,
            currentUserUuid,
            codeReviewerFeedbackAttributes
          )
        );
      };

      if (canLikeComments) {
        commentActions.push(
          <CommentAction onClick={handleOnLikeCommentActionClick}>
            <PullRequestCommentLikeButton
              commentId={commentId}
              commentLikes={commentLikes}
              currentUserUuid={currentUserUuid}
            />
          </CommentAction>
        );
      }
      if (getCommentLikers(comment.commentId, commentLikes).length !== 0) {
        commentActions.push(
          <CommentAction>
            <PullRequestCommentLikers
              commentId={commentId}
              commentLikes={commentLikes}
            />
          </CommentAction>
        );
      }

      return commentActions;
    },
    [
      canLikeComments,
      commentLikes,
      currentUserUuid,
      isArFeedbackEnabled,
      pullrequest,
      dispatch,
      commentUUIDs,
    ]
  );

  return renderActions;
};

export const useCommentTaskActions = () => {
  const pullrequest = useSelector(getCurrentPullRequest);
  const dispatch = useDispatch();
  const intl = useIntl();
  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  const canCreateTask = useSelector(getCanCreateTask) && isOpenPr;

  const isCodeReviewerFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );

  const renderActions: RenderAdditionalCommentActions = useCallback(
    (CommentAction: React.ComponentType<any>, comment: FabricComment) => {
      const commentActions: JSX.Element[] = [];
      const { commentId } = comment;

      const handleOnCreateTaskActionClick = () => {
        const selectedCommentText = getSelectedCommentText(commentId);

        const codeReviewerFeedbackSentiment =
          identifyCodeReviewerComment(comment.createdBy, commentUUIDs) &&
          isCodeReviewerFeedbackEnabled &&
          pullrequest
            ? {
                isCodeReviewer: true,
                codeReviewerFeedbackSentiment: 'positive',
                prId: pullrequest.id,
                prURL: pullrequest.links.html.href,
                prAuthorId: pullrequest.author?.account_id,
                commentId,
                repositoryUUID: pullrequest.destination.repository.uuid,
                hasCodeSuggestions: commentHasCodeSuggestions(comment),
              }
            : undefined;

        publishUiEvent({
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: 'createTaskFromPrCommentButton',
          source: 'pullRequestScreen',
          attributes: codeReviewerFeedbackSentiment,
        });

        dispatch({
          type: TOGGLE_CREATE_COMMENT_TASK_INPUT,
          payload: {
            commentId,
            isCreating: true,
            selectedCommentText,
          },
        });
      };

      if (canCreateTask) {
        commentActions.push(
          <CommentAction
            key="create-task"
            onClick={handleOnCreateTaskActionClick}
          >
            {intl.formatMessage(messages.createTaskLabel)}
          </CommentAction>
        );
      }

      return commentActions;
    },
    [
      canCreateTask,
      dispatch,
      intl,
      isCodeReviewerFeedbackEnabled,
      pullrequest,
      commentUUIDs,
    ]
  );

  return renderActions;
};

export const useCommentJiraActions = () => {
  const pullrequest = useSelector(getCurrentPullRequest);
  const createJiraIssue = useCreateJiraIssueAction();

  const isArFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  const renderActions: RenderAdditionalCommentActions = useCallback(
    (CommentAction: React.ComponentType<any>, comment: FabricComment) => {
      const commentActions: JSX.Element[] = [];
      const { commentId } = comment;

      const codeReviewerFeedbackAttributes =
        isArFeedbackEnabled && pullrequest
          ? {
              isCodeReviewer: true,
              codeReviewerFeedbackSentiment: 'positive',
              prId: pullrequest.id,
              prURL: pullrequest.links.html.href,
              prAuthorId: pullrequest.author?.account_id,
              commentId,
              repositoryUUID: pullrequest.destination.repository.uuid,
              hasCodeSuggestions: commentHasCodeSuggestions(comment),
            }
          : undefined;

      const handleCreateJiraIssueActionClick = () => {
        createJiraIssue.onClick(
          {
            commentId,
            isVisible: true,
          },
          codeReviewerFeedbackAttributes
        );
      };

      if (createJiraIssue.isVisible) {
        commentActions.push(
          <div data-testid="pr-comment-create-jira-issue-action">
            <CommentAction onClick={handleCreateJiraIssueActionClick}>
              {createJiraIssue.label}
            </CommentAction>
          </div>
        );
      }

      return commentActions;
    },
    [createJiraIssue, isArFeedbackEnabled, pullrequest]
  );

  return renderActions;
};

export const usePullRequestCommentActions = (
  threads: CommentThread[],
  showLikeActionOnly?: boolean
) => {
  const renderLikeActions = useCommentLikeActions(threads);
  const renderTaskActions = useCommentTaskActions();
  const renderJiraActions = useCommentJiraActions();

  const renderActions: RenderAdditionalCommentActions = useCallback(
    (CommentAction: any, comment: FabricComment) => {
      if (showLikeActionOnly) {
        return renderLikeActions(CommentAction, comment);
      } else {
        return [
          ...renderLikeActions(CommentAction, comment),
          ...renderTaskActions(CommentAction, comment),
          ...renderJiraActions(CommentAction, comment),
        ];
      }
    },
    [
      renderLikeActions,
      renderTaskActions,
      renderJiraActions,
      showLikeActionOnly,
    ]
  );
  return renderActions;
};
